/**
 * ImportPreview — the Assistant's side-by-side file import view, bound to the
 * platform. The view itself is shared with the downloaded runtime and J2EE
 * (`@twasta/page-runtime/txn/capture`); this wrapper supplies the platform's
 * grid runtime and, by default, the Assistant's staged-import endpoint.
 */
import type { ComponentProps } from 'react'
import {
  ImportPreview as SharedImportPreview, type ImportOutcome, type ImportPreviewData,
} from '@twasta/page-runtime/txn/capture'
import { assistantImportFile, assistantImportLearn, assistantImportPreview } from '@/api/assistant'
import { usePlatformImportRuntime } from './importRuntime'

export type { ImportOutcome } from '@twasta/page-runtime/txn/capture'

export function ImportPreview({ project, importId, objLabel, onClose, fetchPreview, fetchFile, onLearn }: {
  project: string
  importId: string
  objLabel?: string
  onClose: (outcome: ImportOutcome | null) => void
  /** Defaults to the Assistant's staged import (`/api/assistant/import/{id}`). */
  fetchPreview?: () => Promise<ImportPreviewData>
  /** The uploaded document itself. Defaults to the Assistant's
   *  `/api/assistant/import/{id}/file` when the preview is the Assistant's own. */
  fetchFile?: (page?: number) => Promise<Blob>
  /** Defaults to the Assistant's `/api/assistant/import/{id}/learn` when the
   *  preview is the Assistant's own (no custom `fetchPreview`). */
  onLearn?: ComponentProps<typeof SharedImportPreview>['onLearn']
}) {
  const captureRuntime = usePlatformImportRuntime(project)
  return (
    <SharedImportPreview
      project={project}
      importId={importId}
      objLabel={objLabel}
      onClose={onClose}
      captureRuntime={captureRuntime}
      fetchPreview={fetchPreview ?? (() => assistantImportPreview(project, importId))}
      fetchFile={fetchFile ?? (fetchPreview ? undefined : (page) => assistantImportFile(project, importId, page))}
      onLearn={onLearn ?? (fetchPreview ? undefined
        : (body) => assistantImportLearn(project, importId, body))}
    />
  )
}
